import { Link } from 'react-router';
import { formatDate, formatMoney, formatNumber } from '@awj/i18n';
import { Skeleton } from '@awj/ui';

import { ExportMenu } from '~/components/ExportMenu.tsx';
import { Icon } from '~/components/Icon.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { StatCard } from '~/components/StatCard.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { orderColumns } from '~/lib/export-columns.ts';
import {
  useCustomerCount,
  useDashboard,
  useInquiryCounts,
  usePendingReviewCount,
  useProductCount,
} from '~/lib/queries.ts';
import { useRowLink } from '~/lib/row-link.ts';

/**
 * What is happening in the shop, and what is waiting on someone.
 *
 * Two rows, and the split is the point. The first is how trade is going — five cards to glance at,
 * each with its glyph and a soft wave of its colour. The second is work: one panel of queues, each
 * a number and a way into it, and a queue at zero is greyed rather than hidden, so the panel does
 * not reshuffle as the day goes on and staff learn where to look. Only stock running out (red) and
 * refunds owed (amber) colour their figure.
 *
 * The page opens on its own heading — a glyph tile, the name and a line under it — rather than the
 * shared one, which deliberately carries no line: this is the one page that is read as a summary.
 */
export function DashboardPage() {
  const { t, locale } = useI18n();

  const dashboard = useDashboard();
  const rowLink = useRowLink();
  const products = useProductCount();
  const customers = useCustomerCount();
  const reviews = usePendingReviewCount();
  const inquiries = useInquiryCounts();

  const data = dashboard.data;
  const newInquiries = inquiries.data?.New ?? 0;

  if (dashboard.isError) {
    return (
      <>
        <PageTitle title={t('dashboard.title')} />
        <div className="empty-panel">
          <p>{t('common.somethingWentWrong')}</p>
          <button type="button" className="awj-button awj-button--secondary" onClick={() => dashboard.refetch()}>
            {t('common.retry')}
          </button>
        </div>
      </>
    );
  }

  // A queue at zero steps its figure back; otherwise it reads plain, unless it says otherwise.
  const queue = (count: number | undefined, loud: 'plain' | 'warning' | 'danger' = 'plain') =>
    count ? loud : 'quiet';

  return (
    <div className="dash">
      <header className="dash-head">
        <span className="dash-head__icon" aria-hidden="true">
          <Icon name="chart" size={20} />
        </span>
        <div className="dash-head__titles">
          <h1 className="dash-head__title">{t('dashboard.title')}</h1>
          <p className="dash-head__sub">{t('dashboard.subtitle')}</p>
        </div>
      </header>

      <section aria-labelledby="trade-title">
        {/* Named for a screen reader; the cards are their own heading on screen. */}
        <h2 className="sr-only" id="trade-title">
          {t('dashboard.trade')}
        </h2>

        <div className="kpi-grid">
          <StatCard
            label={t('dashboard.revenueThisMonth')}
            value={data ? formatMoney(locale, data.revenueThisMonth, { grouped: true }) : null}
            loading={dashboard.isPending}
            icon="stack"
            colour="violet"
            wave
          />
          <StatCard
            label={t('dashboard.ordersThisMonth')}
            value={data ? formatNumber(locale, data.ordersThisMonth) : null}
            loading={dashboard.isPending}
            icon="cart"
            colour="blue"
            wave
          />
          <StatCard
            label={t('dashboard.revenueToday')}
            value={data ? formatMoney(locale, data.revenueToday, { grouped: true }) : null}
            loading={dashboard.isPending}
            icon="today"
            colour="green"
            wave
          />
          <StatCard
            label={t('dashboard.products')}
            value={products.data !== undefined ? formatNumber(locale, products.data) : null}
            loading={products.isPending}
            icon="box"
            colour="orange"
            wave
            to="/products"
          />
          <StatCard
            label={t('dashboard.customers')}
            value={customers.data !== undefined ? formatNumber(locale, customers.data) : null}
            loading={customers.isPending}
            icon="people"
            colour="sky"
            wave
            to="/customers"
          />
        </div>
      </section>

      <section aria-labelledby="queues-title">
        <div className="dash-section__head">
          <span className="dash-section__icon" aria-hidden="true">
            <Icon name="target" size={16} />
          </span>
          <h2 className="dash-section__title" id="queues-title">
            {t('dashboard.needsAttention')}
          </h2>
        </div>

        <div className="stat-strip" data-count="7">
          <StatCard
            label={t('dashboard.pendingPayment')}
            value={data ? formatNumber(locale, data.pendingPayment) : null}
            loading={dashboard.isPending}
            icon="wallet"
            colour="orange"
            tone={queue(data?.pendingPayment)}
            to="/orders?status=PendingPayment"
          />
          <StatCard
            label={t('dashboard.awaitingFulfilment')}
            value={data ? formatNumber(locale, data.awaitingFulfilment) : null}
            loading={dashboard.isPending}
            icon="box"
            colour="violet"
            tone={queue(data?.awaitingFulfilment)}
            // Paid and preparing are both counted; the list filters by one status, and the paid
            // orders are the ones nobody has picked up yet.
            to="/orders?status=Paid"
          />
          <StatCard
            label={t('dashboard.lowStock')}
            value={data ? formatNumber(locale, data.lowStockProducts) : null}
            loading={dashboard.isPending}
            icon="warning"
            colour="red"
            tone={queue(data?.lowStockProducts, 'danger')}
            // Least stock first — the closest the list comes to "these ones".
            to="/products?sort=stock"
          />
          <StatCard
            label={t('dashboard.pendingReviews')}
            value={reviews.data !== undefined ? formatNumber(locale, reviews.data) : null}
            loading={reviews.isPending}
            icon="star"
            colour="sky"
            tone={queue(reviews.data)}
            to="/reviews"
          />
          <StatCard
            label={t('dashboard.newInquiries')}
            value={inquiries.data ? formatNumber(locale, newInquiries) : null}
            loading={inquiries.isPending}
            icon="chat"
            colour="blue"
            tone={queue(newInquiries)}
            to="/inquiries?status=New"
          />
          <StatCard
            label={t('dashboard.refundsDue')}
            value={data ? formatNumber(locale, data.refundsDue) : null}
            loading={dashboard.isPending}
            icon="refund"
            colour="orange"
            tone={queue(data?.refundsDue, 'warning')}
            to="/orders?status=RefundDue"
          />
          <StatCard
            label={t('dashboard.shippedToday')}
            value={data ? formatNumber(locale, data.shippedToday) : null}
            loading={dashboard.isPending}
            icon="truck"
            colour="violet"
            tone={queue(data?.shippedToday)}
          />
        </div>
      </section>

      <section aria-labelledby="recent-title">
        <div className="dash-section__head">
          <span className="dash-section__icon" aria-hidden="true">
            <Icon name="clock" size={16} />
          </span>
          <h2 className="dash-section__title" id="recent-title">
            {t('dashboard.recentOrders')}
          </h2>
          <div className="dash-section__actions">
            <ExportMenu
              file="recent-orders"
              sheet={t('dashboard.recentOrders')}
              columns={orderColumns(t, locale)}
              rows={() => data?.recentOrders ?? []}
              variant="ghost"
              disabled={!data?.recentOrders.length}
            />
            <Link className="panel-section__more" to="/orders">
              {t('common.viewAll')}
            </Link>
          </div>
        </div>

        {dashboard.isPending ? (
          <Skeleton height="180px" />
        ) : !data || data.recentOrders.length === 0 ? (
          <div className="empty-panel">
            <p>{t('dashboard.noOrdersYet')}</p>
          </div>
        ) : (
          <div className="data-table">
  <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('a11y.scrollableTable')}>
              <table className="data-table__table">
                <thead>
                  <tr>
                    <th className="data-table__th" scope="col">{t('orders.number')}</th>
                    <th className="data-table__th" scope="col">{t('orders.customer')}</th>
                    <th className="data-table__th" scope="col">{t('orders.placed')}</th>
                    <th className="data-table__th" scope="col">{t('orders.status')}</th>
                    <th scope="col" className="data-table__th data-table__cell--end">
                      {t('orders.total')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentOrders.map((order) => (
                    <tr key={order.number} {...rowLink(`/orders/${order.number}`)}>
                      <td className="data-table__td">
                        <Link className="data-table__link mono" to={`/orders/${order.number}`}>
                          {order.number}
                        </Link>
                      </td>
                      <td className="data-table__td">
                        <bdi>{order.customerName}</bdi>
                        <span className="data-table__sub">
                          <bdi>{order.email}</bdi>
                        </span>
                      </td>
                      <td className="data-table__td">
                        <bdi>{formatDate(locale, order.placedAt)}</bdi>
                      </td>
                      <td className="data-table__td">
                        <span className={`status status--${order.status.toLowerCase()}`}>
                          {t(`orderStatus.${order.status}` as 'orderStatus.Paid')}
                        </span>
                      </td>
                      <td className="data-table__td data-table__cell--end">
                        <bdi>{formatMoney(locale, order.grandTotal)}</bdi>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
